import { Box, styled } from '@mui/material';
import { useMemo } from 'react';

import { HubsViewMode, useHorizontalRowScroll } from '#shared';

import {
  EUROPEAN_FOCUS_VIEWS,
  EUROPEAN_HUBS,
} from '#features/GeoMap/constants';

import { useRegionedSurgeonGroups } from '../../data-context';
import { EFocusView } from '../../GeoMap';
import { CaseRotationStatusesBar } from '../../MapStatusBar';
import {
  HubDrawer,
  SurgeonDrawer,
  useHubDrawerState,
  useSurgeonDrawerState,
} from '../../ResourceDrawer';

import { HubsAtCoordinates } from './HubsAtCoordinates';
import { HubsRegionClusters } from './HubsRegionClusters';
import { mainMapOverlayClasses } from './MapOverlay.styled';
import { SurgeonsAtCoordinates } from './SurgeonsAtCoordinates';

import type { HubReportStatus } from '#entities';
import type { AppDataErrors } from '#shared';

interface OverlayFooterPanelProps {
  lastUpdateTime: Date | null;
  appDataErrors: AppDataErrors;
  hubs: HubReportStatus[];
  hubsViewMode: HubsViewMode;
  withTimerInside: boolean;
  isProjectionChange: boolean;
  renderInCentalWidget?: boolean;
  initialFocus: EFocusView;
}

const RotationStatuses = styled(Box, {
  shouldForwardProp: prop => prop !== 'isRegions',
})<{ isRegions: boolean }>(({ theme: { spacing }, isRegions }) => ({
  mx: isRegions ? 0 : 'auto',
  width: '100%',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  overscrollBehaviorX: 'contain',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  position: isRegions ? 'absolute' : 'relative',
  bottom: isRegions ? spacing(14) : 'auto',
  left: isRegions ? '50%' : 'auto',
  transform: isRegions ? 'translateX(-50%)' : 'none',
  overflowY: 'hidden',
  touchAction: 'pan-y',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const MapOverlayFooterPanel = ({
  hubs,
  hubsViewMode,
  isProjectionChange,
  initialFocus,
  renderInCentalWidget = false,
}: OverlayFooterPanelProps) => {
  const rotScrollRef = useHorizontalRowScroll<HTMLDivElement>();
  const regionSurgeons = useRegionedSurgeonGroups();
  const hubDrawer = useHubDrawerState();
  const surgeonDrawer = useSurgeonDrawerState();
  const isFlat = hubsViewMode === HubsViewMode.Flat;
  const isRegions = hubsViewMode === HubsViewMode.Regions;
  const isAround = hubsViewMode === HubsViewMode.Around;
  const isEuropeanFocus = EUROPEAN_FOCUS_VIEWS.includes(initialFocus);

  const hubsToRender = useMemo(() => {
    if (isEuropeanFocus) {
      return hubs.filter(hub => EUROPEAN_HUBS.includes(hub.id));
    }
    return hubs.filter(hub => !EUROPEAN_HUBS.includes(hub.id));
  }, [hubs, isEuropeanFocus]);

  if (isEuropeanFocus) {
    return (
      <>
        <HubsAtCoordinates hubs={hubsToRender} onClickHub={hubDrawer.open} />
        <HubDrawer
          isOpen={Boolean(hubDrawer.selectedHubId)}
          onClose={hubDrawer.close}
          hubId={hubDrawer.selectedHubId}
        />
      </>
    );
  }

  if (!renderInCentalWidget) {
    return null;
  }

  return (
    <>
      <Box className={mainMapOverlayClasses.flatView}>
        <RotationStatuses isRegions={isRegions} ref={rotScrollRef}>
          <CaseRotationStatusesBar />
        </RotationStatuses>

        <Box className={isFlat ? mainMapOverlayClasses.flatViewSubWrapper : ''}>
          {isFlat && (
            <HubsRegionClusters
              hubs={hubsToRender}
              hubsViewMode={hubsViewMode}
              onClickSurgeon={surgeonDrawer.open}
              onClickHub={hubDrawer.open}
            />
          )}
        </Box>
      </Box>

      {isRegions && (
        <HubsRegionClusters
          hubs={hubsToRender}
          hubsViewMode={hubsViewMode}
          onClickSurgeon={surgeonDrawer.open}
          onClickHub={hubDrawer.open}
        />
      )}

      {isAround && !isProjectionChange && (
        <>
          <HubsAtCoordinates hubs={hubs} onClickHub={hubDrawer.open} />
          <SurgeonsAtCoordinates
            regionedSurgeons={regionSurgeons}
            onClickSurgeon={surgeonDrawer.open}
          />
        </>
      )}

      <HubDrawer
        isOpen={Boolean(hubDrawer.selectedHubId)}
        onClose={hubDrawer.close}
        hubId={hubDrawer.selectedHubId}
      />

      <SurgeonDrawer
        isOpen={Boolean(surgeonDrawer.selectedRegionName)}
        onClose={surgeonDrawer.close}
        regionName={surgeonDrawer.selectedRegionName ?? 'n/a'}
      />
    </>
  );
};
